import { SpreadsheetFormatActions } from '../const/ActionsConst';
import { createReducer } from '@common/utils/redux';

/**
 * Normalizes spreadsheet type to match the structure keys
 * Converts "individual" -> "Individual", "pedigree" -> "Pedigree"
 * Also handles case variations
 */
function normalizeSpreadsheetType(spreadsheetType) {
    if (!spreadsheetType) {
        return null;
    }
    
    const typeStr = String(spreadsheetType).toLowerCase();
    if (typeStr === 'individual' || typeStr === 'iss') {
        return 'Individual';
    }
    if (typeStr === 'pedigree' || typeStr === 'pss') {
        return 'Pedigree';
    }
    
    // If already in correct format (Individual/Pedigree), return as is
    if (spreadsheetType === 'Individual' || spreadsheetType === 'Pedigree') {
        return spreadsheetType;
    }
    
    // Default fallback
    return 'Individual';
}

/**
 * Initial state for spreadsheet format data
 * Structure: { 
 *   Individual: { [spreadsheetId]: { format, columnModel, queryModel, folder, filter, timestamp } },
 *   Pedigree: { [spreadsheetId]: { format, columnModel, queryModel, folder, filter, timestamp } }
 * }
 */
const initialState = {
    // Direct structure: spreadsheet type -> spreadsheet ID -> format data
    // Example: { 
    //   Individual: {
    //     123: { 
    //       format: {...}, 
    //       columnModel: {...}, 
    //       queryModel: {...}, 
    //       folder: {...},
    //       filter: {...},
    //       timestamp: 1234567890 
    //     }
    //   },
    //   Pedigree: {
    //     456: { ... }
    //   }
    // }
    Individual: {},
    Pedigree: {}
};

export default createReducer(
    initialState,
    {
        /**
         * Store format data for a specific spreadsheet ID
         * payload: { spreadsheetId, spreadsheetType, formatData: { format, columnModel, queryModel, timestamp } }
         */
        [SpreadsheetFormatActions.STORE_FORMAT_DATA]: (state, payload) => {
            const { spreadsheetId, spreadsheetType, formatData } = payload;
            if (spreadsheetId == null || spreadsheetId === undefined || !spreadsheetType) {
                return state;
            }
            
            const normalizedType = normalizeSpreadsheetType(spreadsheetType);
            const typeData = state[normalizedType] || {};
            
            return {
                ...state,
                [normalizedType]: {
                    ...typeData,
                    [spreadsheetId]: formatData
                }
            };
        },

        /**
         * Update format data for a specific spreadsheet ID
         * payload: { spreadsheetId, spreadsheetType, formatData: { format, columnModel, queryModel, timestamp } }
         */
        [SpreadsheetFormatActions.UPDATE_FORMAT_DATA]: (state, payload) => {
            const { spreadsheetId, spreadsheetType, formatData } = payload;
            if (spreadsheetId == null || spreadsheetId === undefined || !spreadsheetType) {
                return state;
            }
            
            const normalizedType = normalizeSpreadsheetType(spreadsheetType);
            const typeData = state[normalizedType] || {};
            const existingData = typeData[spreadsheetId] || {};
            
            return {
                ...state,
                [normalizedType]: {
                    ...typeData,
                    [spreadsheetId]: {
                        ...existingData,
                        ...formatData,
                        timestamp: Date.now() // Update timestamp on update
                    }
                }
            };
        },

        /**
         * Clear format data for a specific spreadsheet ID
         * payload: { spreadsheetId, spreadsheetType }
         */
        [SpreadsheetFormatActions.CLEAR_FORMAT_DATA]: (state, payload) => {
            const { spreadsheetId, spreadsheetType } = payload;
            if (spreadsheetId == null || spreadsheetId === undefined || !spreadsheetType) {
                return state;
            }
            
            const normalizedType = normalizeSpreadsheetType(spreadsheetType);
            const typeData = state[normalizedType] || {};
            
            if (!typeData[spreadsheetId]) {
                return state;
            }
            
            const { [spreadsheetId]: removed, ...rest } = typeData;
            
            return {
                ...state,
                [normalizedType]: rest
            };
        },

        /**
         * Clear all format data
         */
        [SpreadsheetFormatActions.CLEAR_ALL_FORMAT_DATA]: (state) => ({
            ...state,
            Individual: {},
            Pedigree: {}
        }),

        /**
         * Store folder data for a specific spreadsheet ID
         * payload: { spreadsheetId, spreadsheetType, folder: FolderPrimaryListDto }
         */
        [SpreadsheetFormatActions.STORE_FOLDER_DATA]: (state, payload) => {
            const { spreadsheetId, spreadsheetType, folder } = payload;
            if (spreadsheetId == null || spreadsheetId === undefined || !spreadsheetType) {
                return state;
            }
            
            const normalizedType = normalizeSpreadsheetType(spreadsheetType);
            const typeData = state[normalizedType] || {};
            const existingData = typeData[spreadsheetId] || {};
            
            return {
                ...state,
                [normalizedType]: {
                    ...typeData,
                    [spreadsheetId]: {
                        ...existingData,
                        folder,
                        timestamp: Date.now()
                    }
                }
            };
        },

        /**
         * Store filter data for a specific spreadsheet ID
         * payload: { spreadsheetId, spreadsheetType, filter: { whereClauses: [...], andOR?: number } }
         */
        [SpreadsheetFormatActions.STORE_FILTER_DATA]: (state, payload) => {
            const { spreadsheetId, spreadsheetType, filter } = payload;
            if (spreadsheetId == null || spreadsheetId === undefined || !spreadsheetType) {
                return state;
            }
            
            const normalizedType = normalizeSpreadsheetType(spreadsheetType);
            const typeData = state[normalizedType] || {};
            const existingData = typeData[spreadsheetId] || {};

            const shouldRemoveFilter =
                filter === undefined ||
                (filter && Array.isArray(filter.whereClauses) && filter.whereClauses.length === 0);

            if (shouldRemoveFilter) {
                const cleanedData = { ...existingData };
                delete cleanedData.filter;
                delete cleanedData.timestamp;

                const hasOtherKeys = Object.keys(cleanedData).length > 0;
                if (!hasOtherKeys) {
                    const { [spreadsheetId]: removed, ...rest } = typeData;
                    return {
                        ...state,
                        [normalizedType]: rest
                    };
                }

                return {
                    ...state,
                    [normalizedType]: {
                        ...typeData,
                        [spreadsheetId]: cleanedData
                    }
                };
            }
            
            return {
                ...state,
                [normalizedType]: {
                    ...typeData,
                    [spreadsheetId]: {
                        ...existingData,
                        filter,
                        timestamp: Date.now()
                    }
                }
            };
        },

        /**
         * Update folder data for a specific spreadsheet ID
         * payload: { spreadsheetId, spreadsheetType, folder: FolderPrimaryListDto }
         */
        [SpreadsheetFormatActions.UPDATE_FOLDER_DATA]: (state, payload) => {
            const { spreadsheetId, spreadsheetType, folder } = payload;
            if (spreadsheetId == null || spreadsheetId === undefined || !spreadsheetType) {
                return state;
            }
            
            const normalizedType = normalizeSpreadsheetType(spreadsheetType);
            const typeData = state[normalizedType] || {};
            
            if (!typeData[spreadsheetId]) {
                return state;
            }
            
            return {
                ...state,
                [normalizedType]: {
                    ...typeData,
                    [spreadsheetId]: {
                        ...typeData[spreadsheetId],
                        folder,
                        timestamp: Date.now()
                    }
                }
            };
        },

        /**
         * Update filter data for a specific spreadsheet ID
         * payload: { spreadsheetId, spreadsheetType, filter: { whereClauses: [...], andOR?: number } }
         */
        [SpreadsheetFormatActions.UPDATE_FILTER_DATA]: (state, payload) => {
            const { spreadsheetId, spreadsheetType, filter } = payload;
            if (spreadsheetId == null || spreadsheetId === undefined || !spreadsheetType) {
                return state;
            }
            
            const normalizedType = normalizeSpreadsheetType(spreadsheetType);
            const typeData = state[normalizedType] || {};
            
            if (!typeData[spreadsheetId]) {
                return state;
            }
            
            const existingData = typeData[spreadsheetId];
            const shouldRemoveFilter =
                filter === undefined ||
                (filter && Array.isArray(filter.whereClauses) && filter.whereClauses.length === 0);

            if (shouldRemoveFilter) {
                const cleanedData = { ...existingData };
                delete cleanedData.filter;
                delete cleanedData.timestamp;

                const hasOtherKeys = Object.keys(cleanedData).length > 0;
                if (!hasOtherKeys) {
                    const { [spreadsheetId]: removed, ...rest } = typeData;
                    return {
                        ...state,
                        [normalizedType]: rest
                    };
                }

                return {
                    ...state,
                    [normalizedType]: {
                        ...typeData,
                        [spreadsheetId]: cleanedData
                    }
                };
            }
            
            return {
                ...state,
                [normalizedType]: {
                    ...typeData,
                    [spreadsheetId]: {
                        ...typeData[spreadsheetId],
                        filter,
                        timestamp: Date.now()
                    }
                }
            };
        },
    }
);

